import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import React from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import {
  deleteCampaign as apiDeleteCampaign,
  terminateCampaign,
  updateCampaignEndDate,
  writeCampaign,
} from 'apis/restaurant-api/merchant-admin/campaigns/api';
import { Campaign, CampaignType, DiscountValue } from 'apis/restaurant-api/merchant-admin/campaigns/types';

import { useCampaignsRoutes } from './useCampaignsRoutes';
import { CampaignsQueryKeys } from '../campaignsQueryKeys';
import {
  BasketDiscountFormValues,
  DeliveryDiscountFormValues,
  EndOfDayFormValues,
  ItemDiscountFormValues,
  VenuesValidationError,
} from '../types';
import {
  telemetryNewCampaignDataCreateCampaign,
  telemetryUpdateCampaignDataEndCampaign,
  telemetryUpdateCampaignDataUpdateCampaign,
} from '../utils/telemetry/telemetry';
import { transformBasketDiscountFormToApi } from '../utils/transformFormToApi/transformBasketDiscountFormToApi';
import { transformDeliveryDiscountFormToApi } from '../utils/transformFormToApi/transformDeliveryDiscountFormToApi';
import { transformItemDiscountFormToApi } from '../utils/transformFormToApi/transformItemDiscountFormToApi';

type ErrorResponse = {
  data: null | {
    reason: string;
    venue_ids: string[];
  };
  error_code: 400 | number;
  error_reason: 'validation_failed' | string;
  msg: string;
  params: null;
  status: 'ERR' | string;
};

type UseCampaignMutationsProps = {
  merchantId: string;
  venueIds: string[];
  onInvalidVenuesError?(errorProps: VenuesValidationError): void;
};

export const useCampaignMutations = ({ merchantId, venueIds, onInvalidVenuesError }: UseCampaignMutationsProps) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const campaignsRoutes = useCampaignsRoutes();

  const deleteCampaign = useMutation(apiDeleteCampaign, {
    onSuccess: async () => {
      toast.success(<FormattedMessage id="campaigns.success-delete" />);
      await queryClient.invalidateQueries(CampaignsQueryKeys.GET_ALL(merchantId, venueIds));
    },
    onError: () => {
      toast.error(<FormattedMessage id="campaigns.error-delete" />);
    },
  });

  const terminate = useMutation(terminateCampaign, {
    onSuccess: async (res: Campaign) => {
      toast.success(<FormattedMessage id="campaigns.success-terminate" />);

      telemetryUpdateCampaignDataEndCampaign(res.campaign_type);

      await queryClient.invalidateQueries(CampaignsQueryKeys.GET_ALL(merchantId, venueIds));
      await queryClient.invalidateQueries(CampaignsQueryKeys.GET_SINGLE(merchantId, res.id));
    },
    onError: () => {
      toast.error(<FormattedMessage id="campaigns.error-terminate" />);
    },
  });

  const updateEndDate = useMutation(updateCampaignEndDate, {
    onSuccess: async res => {
      toast.success(<FormattedMessage id="campaigns.success-update" />);
      await queryClient.invalidateQueries(CampaignsQueryKeys.GET_ALL(merchantId, venueIds));
      await queryClient.invalidateQueries(CampaignsQueryKeys.GET_SINGLE(merchantId, res.id));
      history.push(campaignsRoutes.details(res.id));
    },
    onError: () => {
      toast.error(<FormattedMessage id="campaigns.error-update" />);
    },
  });

  const update = useMutation(
    (
      values:
        | (DeliveryDiscountFormValues & {
            deliveryDiscount: DiscountValue;
            currency: Currency;
          })
        | (BasketDiscountFormValues & { currency: Currency })
        | (ItemDiscountFormValues & { currency: Currency; isMultiItemCampaignSupported: boolean }),
    ) => {
      if (values.campaignType === CampaignType.DELIVERY_DISCOUNT) {
        const props = transformDeliveryDiscountFormToApi({
          ...values,
          merchantId,
          venueIds: values.venueIds ?? venueIds,
        });
        return writeCampaign(props);
      }

      if (values.campaignType === CampaignType.ITEM_DISCOUNT) {
        const { itemTriggerCount, itemIds, categoryIds } = values;
        if (!itemIds.length && !categoryIds.length) {
          return Promise.reject('The campaign must include at least one item.');
        }

        if (itemTriggerCount === undefined) {
          return Promise.reject('itemTriggerCount cannot be undefined');
        }

        const props = transformItemDiscountFormToApi({ ...values, itemTriggerCount, merchantId, venueIds });
        return writeCampaign(props);
      }

      const props = transformBasketDiscountFormToApi({ ...values, merchantId, venueIds: values.venueIds ?? venueIds });
      return writeCampaign(props);
    },
    {
      onSuccess: async (res: Campaign) => {
        toast.success(<FormattedMessage id="campaigns.success-update" />);

        telemetryUpdateCampaignDataUpdateCampaign({
          campaignType: res.campaign_type,
        });

        await queryClient.invalidateQueries(CampaignsQueryKeys.GET_ALL(merchantId, venueIds));
        history.push(campaignsRoutes.details(res.id));
      },
      onError: () => {
        toast.error(<FormattedMessage id="campaigns.error-update" />);
      },
    },
  );

  const create = useMutation(
    (
      values:
        | (DeliveryDiscountFormValues & {
            deliveryDiscount: DiscountValue;
            currency: Currency;
          })
        | (BasketDiscountFormValues & { currency: Currency })
        | (ItemDiscountFormValues & { currency: Currency; isMultiItemCampaignSupported: boolean })
        | (EndOfDayFormValues & { currency: Currency; isMultiItemCampaignSupported: boolean }),
    ) => {
      if (values.campaignType === CampaignType.DELIVERY_DISCOUNT) {
        const props = transformDeliveryDiscountFormToApi({
          ...values,
          venueIds: values.venueIds ?? venueIds,
          merchantId,
        });
        return writeCampaign(props);
      }

      if (values.campaignType === CampaignType.ITEM_DISCOUNT || values.campaignType === CampaignType.END_OF_DAY) {
        const { itemTriggerCount, itemIds, categoryIds } = values;
        if (!itemIds.length && !categoryIds.length) {
          return Promise.reject('The campaign must include at least one item.');
        }

        if (itemTriggerCount === undefined) {
          return Promise.reject('itemTriggerCount cannot be undefined');
        }

        const props = transformItemDiscountFormToApi(
          { ...values, itemTriggerCount, merchantId, venueIds, campaignType: CampaignType.ITEM_DISCOUNT },
          values.campaignType === CampaignType.END_OF_DAY,
        );
        return writeCampaign(props);
      }

      const props = transformBasketDiscountFormToApi({ ...values, merchantId, venueIds: values.venueIds ?? venueIds });
      return writeCampaign(props);
    },
    {
      onSuccess: async (res: Campaign) => {
        const startDateObj = DateTime.fromISO(res.start_date);
        const endDateObj = DateTime.fromISO(res.end_date);
        const campaignDuration = endDateObj.diff(startDateObj, ['days']).toObject().days || 0;

        toast.success(<FormattedMessage id="campaigns.success-create" />);

        telemetryNewCampaignDataCreateCampaign({
          campaignType: res.campaign_type,
          campaignDuration,
        });

        await queryClient.invalidateQueries(CampaignsQueryKeys.GET_ALL(merchantId, venueIds));
        history.push(campaignsRoutes.details(res.id));
      },
      onError: (error: AxiosError<ErrorResponse>) => {
        const errorData = error.response?.data?.data;

        if (errorData?.reason && errorData?.venue_ids) {
          onInvalidVenuesError?.({
            venueIds: errorData.venue_ids,
            reason: errorData.reason,
          });
        } else {
          toast.error(<FormattedMessage id="campaigns.error-create" />);
        }
      },
    },
  );

  return { deleteCampaign, terminate, updateEndDate, update, create };
};
